// FNP-1191 — mirrors the FNP-1184 neutral read schema
// (flexi-domains-api/src/appsync-api/src/schema/features/dns-zones.graphql).
//
//   listDnsZones(connectionId: ID, limit: Int, nextToken: String): DnsZonesConnection
//   getDnsZone(connectionId: ID!, id: ID!): DnsZone
//
// Three rules travel with these types and are enforced by the code below them:
//
//  1. `DnsZone.id` is OPAQUE — Flexi's own id, minted by the adapter, never the
//     provider's. Nothing in this module parses it, splits it or renders it as
//     "the Route 53 zone id". It is passed back verbatim.
//  2. `nextToken != null` is the ONLY end-of-results signal. `limit` applies
//     BEFORE filtering, so an EMPTY page with a live token is normal.
//  3. `getDnsZone` needs BOTH connectionId and id — a zone id is only
//     meaningful against the connection that minted it.

export type DnsProviderKind = 'ROUTE53' | 'CLOUDFLARE' | 'FAKE';

export type DnsZone = {
    id: string,
    name: string,
    connectionId: string,
    provider: DnsProviderKind,
    isPrivate: boolean,
    nameservers: string[],
    // ⚠️ TRI-STATE, and the third state is not cosmetic.
    //
    //   true  — the registrar-side nameservers appear to match this provider's
    //   false — determined, and they do NOT appear to match
    //   null  — NOT DETERMINED (undefined here when the field is absent)
    //
    // Null and false are DIFFERENT. Collapsing them lets the UI report a
    // healthy zone as broken in the minutes after a delegation change, which is
    // exactly what the schema comment warns against. Everything that renders or
    // filters this field branches on all three — see delegationState() below.
    appearsDelegated?: boolean | null,
}

// ⚠️ WHY THIS IS NOT A PLAIN CONNECTION TYPE.
//
// Zones fan out across EVERY connection a tenant has. When one connection is
// broken, the honest answer is not a shorter list: a tenant with three
// connections where one is down must not be shown two connections' zones
// silently, which is indistinguishable from having deleted them.
//
// So `failures` carries the connections that could not be read, and the module
// renders BOTH — the zones that loaded AND a banner naming what did not.
// Partial success is success-with-a-warning, never an error state.
export type DnsZonesConnectionResult = {
    items: DnsZone[],
    nextToken?: string | null,
    // `[DnsConnectionFailure!]!` — EMPTY, never null. Check `.length`.
    failures: DnsConnectionFailure[],
}

export type DnsConnectionFailure = {
    connectionId: string,
    provider: DnsProviderKind,
    // The normalised failure class, e.g. "AUTHENTICATION_FAILED".
    kind: string,
    // The provider's own message, VERBATIM. Never re-worded on the way to the
    // screen — classify for the core, preserve for the human (FNP-1180).
    message: string,
    // A rate limit is retryable; a revoked token is not, and the tenant must go
    // fix something. The banner says which.
    retryable: boolean,
}

export type ListDnsZones = {
    listDnsZones: DnsZonesConnectionResult
}

export type GetDnsZone = {
    getDnsZone: DnsZone
}

// THE TRI-STATE, NAMED.
//
// A string union rather than `boolean | null | undefined` scattered through the
// column, the profile and the filter. Three call sites branching on nullability
// independently is how two of them end up agreeing and the third does not.
export type DelegationState = 'DELEGATED' | 'NOT_DELEGATED' | 'UNDETERMINED';

export const delegationState = (zone: DnsZone): DelegationState => {
    if (zone.appearsDelegated === true) {
        return 'DELEGATED';
    }
    if (zone.appearsDelegated === false) {
        return 'NOT_DELEGATED';
    }
    // null AND undefined land here. "The API did not say" — NOT "not delegated".
    return 'UNDETERMINED';
};

// What a connection's credential proved it could do (FNP-1181
// `ProviderConnectionCapabilities`). The catalog entry is the CEILING; this is
// that ceiling narrowed by the credential — a tenant who handed Flexi a
// read-only Route 53 role has a connection that cannot write.
export type ZoneWriteCapabilities = {
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}

// ─── FNP-1191 CRUD half: the write vocabulary ───

// What `createDnsZone(input: CreateDnsZoneInput!)` takes.
//
// ⚠️ NO `isPrivate`, and the schema is emphatic about why. A private zone is
// VPC-associated; Route 53 wants a VPC id and region at creation and Cloudflare
// has no equivalent. A field only one provider can populate is a provider leak
// wearing a neutral name. `DnsZone.isPrivate` stays a READ-time observation of
// a zone that already exists, which is a different thing — so the create form
// has no visibility control.
export type CreateDnsZoneInput = {
    connectionId: string,
    // WITHOUT a trailing dot. Providers disagree about the dot; the adapter
    // normalises, so nothing here appends one.
    name: string,
    // Free text where the provider supports one; ignored (not refused) where
    // it does not.
    comment?: string,
}

export type DeleteDnsZoneInput = {
    connectionId: string,
    // Flexi's own opaque zone id, never a provider id.
    id: string,
}

// What a delete answers with — a payload rather than a bare Boolean, so a
// caller updating a list knows which row went.
export type DeleteDnsZoneResult = {
    id: string,
    connectionId: string,
}

export type CreateDnsZone = {
    createDnsZone: DnsZone
}

export type DeleteDnsZone = {
    deleteDnsZone: DeleteDnsZoneResult
}

// THE NORMALISED PROVIDER FAILURE CLASSES (FNP-1180), as far as this module
// branches on them. The closed set lives in @flexinet/dns-provider-contract on
// the API side; these are the members the console has distinct copy for.
//
// ⚠️ `ZONE_NOT_EMPTY` IS SEPARATE FROM `VALIDATION_REJECTED` ON PURPOSE, and
// that distinction is the whole reason this union exists here rather than the
// module treating every rejection as one failure. A validation rejection means
// the request was malformed and is NOT retryable — a bad domain name stays bad.
// ZONE_NOT_EMPTY is retryable AFTER A HUMAN DOES SOMETHING: delete the records,
// then delete the zone. A console that cannot tell the two apart either shows
// one message for both, or branches on the raw provider code — which puts a
// Route 53 error name into neutral code and is exactly the leak the opaque
// `FlexiZoneId` exists to prevent.
export type DnsWriteErrorKind =
    | 'AUTHENTICATION_FAILED'
    | 'PERMISSION_DENIED'
    | 'NOT_FOUND'
    | 'RATE_LIMITED'
    | 'PROVIDER_UNAVAILABLE'
    | 'VALIDATION_REJECTED'
    | 'ZONE_NOT_EMPTY'
    // Not a provider class. The transport failed, or the error did not carry a
    // recognisable kind at all — "we do not know", kept distinct from the six
    // things we do know so the copy never claims more than it has evidence for.
    | 'UNKNOWN';

// A provider failure, as this module needs it: the class it branches on, and
// the provider's own words to show the human alongside.
export type DnsWriteError = {
    kind: DnsWriteErrorKind,
    // The provider's message VERBATIM where one survived the trip. Never
    // re-worded — FNP-1087 took a whole investigation because a normalised
    // class alone was all anyone had.
    providerMessage: string,
}

// A connection as the CREATE FORM's picker needs it (FNP-1181). Not the full
// `ProviderConnection` — this module never needs `secretRef`, `accountId` or
// the timestamps, and a narrower type is a narrower selection set.
export type ZoneCreateConnection = {
    id: string,
    // What the tenant named it. The picker shows this, never the opaque id.
    displayName: string,
    // The catalog provider id, e.g. 'ROUTE53'.
    providerId: string,
    // ⚠️ Only 'ACTIVE' may be offered. Any other status is the provider saying
    // the credential does not work NOW, whatever the stored snapshot proved at
    // connect time.
    status: string,
    capabilities?: ZoneWriteCapabilities | null,
}

export type ListProviderConnections = {
    listProviderConnections: {
        items: ZoneCreateConnection[],
        nextToken?: string | null,
    }
}
